import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ArtigoDto, MonthSummary } from '@/types';
import { formatDateShort, formatEur } from '@/lib/format';
import { Spinner } from '@/components/ui/Spinner';

interface ResumoTabProps {
  summary?: MonthSummary;
  loading: boolean;
}

function artigoLabel(a: ArtigoDto): string {
  return `${a.quantidade ? `${a.quantidade}× ` : ''}${a.descricao}`;
}

export function ResumoTab({ summary, loading }: ResumoTabProps) {
  const { t } = useTranslation();
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  if (loading && !summary) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (!summary || summary.numeroGuias === 0) {
    return (
      <div className="rounded-2xl border border-conduril-border bg-white py-12 text-center text-sm text-conduril-muted shadow-card">
        {t('dashboard.empty')}
        <br />
        {t('dashboard.emptyHint')}
      </div>
    );
  }

  const toggle = (obraId: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(obraId)) next.delete(obraId);
      else next.add(obraId);
      return next;
    });

  const visible = summary.obras.filter((o) => !hidden.has(o.obraId));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between rounded-2xl bg-gradient-to-br from-conduril-light to-conduril px-5 py-5 text-white shadow-card">
        <span className="text-sm font-medium opacity-90">{t('dashboard.totalMonth')}</span>
        <span className="text-2xl font-bold tabular-nums sm:text-3xl">{formatEur(summary.total)}</span>
      </div>

      {summary.obras.length > 1 && (
        <div className="rounded-2xl border border-conduril-border bg-white p-3 shadow-card">
          <div className="mb-2.5 flex items-center justify-between px-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-conduril-muted">
              {t('dashboard.filterObras')} · {summary.obras.length}
            </span>
            {hidden.size > 0 && (
              <button
                type="button"
                onClick={() => setHidden(new Set())}
                className="text-xs font-semibold text-conduril transition hover:underline"
              >
                {t('dashboard.showAll')}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[...summary.obras]
              .sort((a, b) => a.obra.localeCompare(b.obra))
              .map((o) => {
                const on = !hidden.has(o.obraId);
                return (
                  <button
                    key={o.obraId}
                    type="button"
                    onClick={() => toggle(o.obraId)}
                    aria-pressed={on}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition ${
                      on
                        ? 'border-conduril/15 bg-conduril-tint text-conduril'
                        : 'border-conduril-border bg-white text-conduril-muted hover:bg-conduril-surface'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-conduril' : 'bg-conduril-border/80'}`} />
                    {o.obra}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {visible.map((o) => (
          <div key={o.obraId} className="overflow-hidden rounded-2xl border border-conduril-border bg-white shadow-card">
            <div className="flex items-center justify-between gap-2 border-b border-conduril-border bg-conduril-tint px-4 py-3 text-[15px] font-bold text-conduril-ink">
              <span className="flex min-w-0 items-center gap-2 truncate">
                <span className="truncate">{o.obra}</span>
                <span className="shrink-0 rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-conduril-muted">
                  {o.guias.length}
                </span>
              </span>
              <span className="whitespace-nowrap tabular-nums">{formatEur(o.total)}</span>
            </div>
            {o.guias.map((g) => (
              <div key={g.id}>
                <div className="flex justify-between gap-2 border-t border-conduril-border/60 bg-conduril-surface/50 px-4 py-2 text-[13px] font-semibold">
                  <span>
                    {t('guias.guiaNo', { numero: g.numero || '—' })} · {formatDateShort(g.data)}
                  </span>
                  <span className="whitespace-nowrap tabular-nums">{formatEur(g.total)}</span>
                </div>
                {g.artigos.map((a, i) => (
                  <div key={i} className="flex justify-between gap-2 border-t border-conduril-border/40 py-2 pl-7 pr-4 text-[13px]">
                    <span className="text-slate-600">{artigoLabel(a)}</span>
                    <span className="whitespace-nowrap font-medium tabular-nums">{formatEur(a.valor)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
